export interface TenantOption {
    code: string;
    name: string;
}

export interface TenantChangePayload {
    tenantCode: string;
    tenantName: string;
    changedAt: number;
}

export const ACTIVE_TENANT_CODE_STORAGE_KEY = 'pulse.activeTenantCode';
export const ACTIVE_TENANT_SESSION_MARKER_STORAGE_KEY = 'pulse.activeTenantSessionMarker';
export const TENANT_CHANGE_BROADCAST_STORAGE_KEY = 'pulse.tenantChangeBroadcast';
export const TENANT_CHANGE_PENDING_SESSION_KEY = 'pulse.tenantChangePending';

const toTenantOption = (tenant: any): TenantOption | null => {
    if (!tenant || typeof tenant.code !== 'string' || !tenant.code.trim()) {
        return null;
    }

    return {
        code: tenant.code,
        name: typeof tenant.name === 'string' && tenant.name.trim() ? tenant.name : tenant.code,
    };
};

export const getTenantOptionsFromUser = (user: any): TenantOption[] => {
    if (!user || !Array.isArray(user.tenants)) {
        return [];
    }

    return user.tenants
        .map(toTenantOption)
        .filter((tenant: TenantOption | null): tenant is TenantOption => tenant !== null);
};

export const getStoredActiveTenantCode = (): string =>
    localStorage.getItem(ACTIVE_TENANT_CODE_STORAGE_KEY) || '';

const getSessionMarker = (): string => localStorage.getItem('accessToken') || '';

export const setActiveTenantCode = (tenantCode: string): boolean => {
    try {
        localStorage.setItem(ACTIVE_TENANT_CODE_STORAGE_KEY, tenantCode);
        return true;
    } catch {
        return false;
    }
};

export const resolveActiveTenantCode = (user: any): string => {
    const options = getTenantOptionsFromUser(user);
    if (options.length === 0) {
        return '';
    }

    const stored = getStoredActiveTenantCode();
    if (stored && options.some((tenant) => tenant.code === stored)) {
        return stored;
    }

    return options[0]?.code || '';
};

export const initializeActiveTenantForSession = (user: any): { activeTenantCode: string; error: string | null } => {
    const options = getTenantOptionsFromUser(user);
    if (options.length === 0) {
        return {
            activeTenantCode: '',
            error: 'Tenant details are unavailable. Please refresh or contact administrator.',
        };
    }

    const marker = getSessionMarker();
    const storedMarker = localStorage.getItem(ACTIVE_TENANT_SESSION_MARKER_STORAGE_KEY) || '';
    const shouldResetForNewSession = marker !== storedMarker;

    const nextTenantCode = shouldResetForNewSession
        ? options[0]?.code || ''
        : resolveActiveTenantCode(user);

    if (!nextTenantCode) {
        return {
            activeTenantCode: '',
            error: 'Unable to resolve the default tenant for this session. Please refresh or contact administrator.',
        };
    }

    try {
        localStorage.setItem(ACTIVE_TENANT_CODE_STORAGE_KEY, nextTenantCode);
        localStorage.setItem(ACTIVE_TENANT_SESSION_MARKER_STORAGE_KEY, marker);
        return {
            activeTenantCode: nextTenantCode,
            error: null,
        };
    } catch {
        return {
            activeTenantCode: nextTenantCode,
            error: 'Unable to save tenant selection in this browser session. Please refresh or contact administration.',
        };
    }
};

export const getTenantName = (tenants: TenantOption[], tenantCode: string): string => {
    return tenants.find((tenant) => tenant.code === tenantCode)?.name || tenantCode;
};

const parseTenantChangePayloadInternal = (value: string | null): TenantChangePayload | null => {
    if (!value) {
        return null;
    }

    try {
        const parsed = JSON.parse(value) as Partial<TenantChangePayload>;
        const tenantCode = typeof parsed.tenantCode === 'string' ? parsed.tenantCode.trim() : '';
        const tenantName = typeof parsed.tenantName === 'string' ? parsed.tenantName.trim() : '';
        const changedAt = typeof parsed.changedAt === 'number' ? parsed.changedAt : 0;

        if (!tenantCode || !tenantName || !changedAt) {
            return null;
        }

        return {
            tenantCode,
            tenantName,
            changedAt,
        };
    } catch {
        return null;
    }
};

export const parseTenantChangePayload = (value: string | null): TenantChangePayload | null =>
    parseTenantChangePayloadInternal(value);

export const publishTenantChange = (tenant: TenantOption): TenantChangePayload | null => {
    if (!tenant?.code || !tenant?.name) {
        return null;
    }

    const payload: TenantChangePayload = {
        tenantCode: tenant.code,
        tenantName: tenant.name,
        changedAt: Date.now(),
    };

    try {
        localStorage.setItem(TENANT_CHANGE_BROADCAST_STORAGE_KEY, JSON.stringify(payload));
        return payload;
    } catch {
        return null;
    }
};

export const setPendingTenantChangeNotice = (payload: TenantChangePayload): void => {
    try {
        sessionStorage.setItem(TENANT_CHANGE_PENDING_SESSION_KEY, JSON.stringify(payload));
    } catch {
        // Intentionally ignored: pending notice is best effort.
    }
};

export const getPendingTenantChangeNotice = (): TenantChangePayload | null => {
    try {
        return parseTenantChangePayloadInternal(sessionStorage.getItem(TENANT_CHANGE_PENDING_SESSION_KEY));
    } catch {
        return null;
    }
};

export const clearPendingTenantChangeNotice = (): void => {
    try {
        sessionStorage.removeItem(TENANT_CHANGE_PENDING_SESSION_KEY);
    } catch {
        // Intentionally ignored.
    }
};

export const clearTenantSessionStorage = (): void => {
    localStorage.removeItem(ACTIVE_TENANT_CODE_STORAGE_KEY);
    localStorage.removeItem(ACTIVE_TENANT_SESSION_MARKER_STORAGE_KEY);
    localStorage.removeItem(TENANT_CHANGE_BROADCAST_STORAGE_KEY);
    clearPendingTenantChangeNotice();
};
